import { useState, useCallback, useEffect, useRef } from 'react';
import { Grid, Snackbar, Alert as MuiAlert, Typography } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import PageTitleWrapper from '@/components/PageTitleWrapper';
import PageHeader from './PageHeader';
import FloorPlansTable from '@/content/Management/Tables/FloorPlansTable';
import FloorPlanDialog from '@/components/dialogs/FloorPlanDialog';
import KdsStationsTable from '@/content/Management/Tables/KdsStationsTable';
import KdsStationDialog, {
  KdsCategoryOption
} from '@/components/dialogs/KdsStationDialog';
import ConfirmDialog from '@/components/modals/ConfirmDialog';
import * as tableService from '@/data/tableService';
import { menuService } from '@/data/menuService';
import type {
  FloorPlan,
  CreateFloorPlanData,
  UpdateFloorPlanData,
  KdsStation,
  CreateKdsStationData,
  UpdateKdsStationData
} from '@/types/Table.interface';
import { useUserViewMode } from '@/hooks/useUserViewMode';

const TableManagement = () => {
  const { t } = useTranslation();
  const { currentBranch } = useUserViewMode();
  const activeBranchId = useRef(currentBranch?.id);
  useEffect(() => {
    activeBranchId.current = currentBranch?.id;
  }, [currentBranch?.id]);

  const [floorPlans, setFloorPlans] = useState<FloorPlan[]>([]);
  const [loadingFloorPlans, setLoadingFloorPlans] = useState(false);

  // Floor plan dialog
  const [floorPlanDialogOpen, setFloorPlanDialogOpen] = useState(false);
  const [editingFloorPlan, setEditingFloorPlan] = useState<FloorPlan | null>(
    null
  );
  const [floorPlanDialogError, setFloorPlanDialogError] = useState<
    string | undefined
  >();
  const [deleteFloorPlanId, setDeleteFloorPlanId] = useState<number | null>(
    null
  );

  // KDS stations
  const [kdsStations, setKdsStations] = useState<KdsStation[]>([]);
  const [loadingKdsStations, setLoadingKdsStations] = useState(false);
  const [kdsCategories, setKdsCategories] = useState<KdsCategoryOption[]>([]);
  const [kdsDialogOpen, setKdsDialogOpen] = useState(false);
  const [editingKdsStation, setEditingKdsStation] = useState<KdsStation | null>(
    null
  );
  const [kdsDialogError, setKdsDialogError] = useState<string | undefined>();
  const [deleteKdsStationId, setDeleteKdsStationId] = useState<number | null>(
    null
  );

  // Notifications
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchFloorPlans = useCallback(async () => {
    const branchId = currentBranch?.id;
    if (!branchId) {
      setFloorPlans([]);
      setLoadingFloorPlans(false);
      return;
    }
    try {
      setLoadingFloorPlans(true);
      const data = await tableService.getFloorPlans({ branchId });
      if (activeBranchId.current === branchId) setFloorPlans(data);
    } catch (err) {
      console.error('Error fetching floor plans:', err);
    } finally {
      if (activeBranchId.current === branchId) setLoadingFloorPlans(false);
    }
  }, [currentBranch]);

  useEffect(() => {
    fetchFloorPlans();
  }, [fetchFloorPlans]);

  const fetchKdsStations = useCallback(async () => {
    const branchId = currentBranch?.id;
    if (!branchId) {
      setKdsStations([]);
      setLoadingKdsStations(false);
      return;
    }
    try {
      setLoadingKdsStations(true);
      const data = await tableService.getKdsStations({ branchId });
      if (activeBranchId.current === branchId) setKdsStations(data);
    } catch (err) {
      console.error('Error fetching KDS stations:', err);
    } finally {
      if (activeBranchId.current === branchId) setLoadingKdsStations(false);
    }
  }, [currentBranch]);

  const fetchKdsCategories = useCallback(async () => {
    try {
      const branchId = currentBranch?.id;
      if (!branchId) {
        setKdsCategories([]);
        return;
      }
      const data = await menuService.getBranchMenuCategories(branchId, {
        status: 'ACTIVE'
      });
      if (activeBranchId.current !== branchId) return;
      setKdsCategories(
        (data || []).map((c: { id: number; name: string }) => ({
          id: c.id,
          name: c.name
        }))
      );
    } catch (err) {
      console.error('Error fetching categories for KDS stations:', err);
    }
  }, [currentBranch]);

  useEffect(() => {
    fetchKdsStations();
    fetchKdsCategories();
  }, [fetchKdsStations, fetchKdsCategories]);

  // ─── Floor Plan CRUD ────────────────────────────────────────────

  const handleOpenFloorPlanDialog = (fp?: FloorPlan) => {
    setEditingFloorPlan(fp ?? null);
    setFloorPlanDialogError(undefined);
    setFloorPlanDialogOpen(true);
  };

  const handleCloseFloorPlanDialog = () => {
    setFloorPlanDialogOpen(false);
    setEditingFloorPlan(null);
    setFloorPlanDialogError(undefined);
  };

  const handleSaveFloorPlan = async (
    data: CreateFloorPlanData & { isActive?: boolean }
  ) => {
    try {
      const branchId = currentBranch?.id;
      const branchParams = branchId ? { branchId } : undefined;
      if (editingFloorPlan) {
        const updated = await tableService.updateFloorPlan(
          editingFloorPlan.id,
          data as UpdateFloorPlanData,
          branchParams
        );
        setFloorPlans((prev) =>
          prev.map((fp) => (fp.id === updated.id ? updated : fp))
        );
        setSuccessMessage(t('table.floor.plan.update.success'));
      } else {
        const created = await tableService.createFloorPlan(data, branchParams);
        setFloorPlans((prev) => [...prev, created]);
        setSuccessMessage(t('table.floor.plan.create.success'));
      }
      setFloorPlanDialogError(undefined);
      setFloorPlanDialogOpen(false);
      setEditingFloorPlan(null);
      setShowSuccess(true);
    } catch (err: any) {
      const FLOOR_PLAN_ERROR_CODES: Record<string, string> = {
        FLOOR_PLAN_DUPLICATE_NAME: 'table.floor.plan.error.duplicate.name',
        FLOOR_PLAN_GRID_SHRINK_OUT_OF_BOUNDS:
          'table.floor.plan.error.grid.shrink',
        FLOOR_PLAN_HAS_OPEN_CHECKS: 'table.floor.plan.error.has.open.checks'
      };
      const errorCode = err?.response?.data?.errorCode;
      const fallback = editingFloorPlan
        ? t('table.floor.plan.error.update')
        : t('table.floor.plan.error.create');
      const msg =
        errorCode && FLOOR_PLAN_ERROR_CODES[errorCode]
          ? t(FLOOR_PLAN_ERROR_CODES[errorCode])
          : fallback;
      setFloorPlanDialogError(msg);
      throw err;
    }
  };

  const handleDeleteFloorPlanConfirm = async () => {
    if (!deleteFloorPlanId) return;
    try {
      const branchId = currentBranch?.id;
      await tableService.deleteFloorPlan(
        deleteFloorPlanId,
        branchId ? { branchId } : undefined
      );
      setFloorPlans((prev) => prev.filter((fp) => fp.id !== deleteFloorPlanId));
      setSuccessMessage(t('table.floor.plan.delete.success'));
      setShowSuccess(true);
    } catch (err: any) {
      const FLOOR_PLAN_ERROR_CODES: Record<string, string> = {
        FLOOR_PLAN_HAS_OPEN_CHECKS: 'table.floor.plan.error.has.open.checks'
      };
      const errorCode = err?.response?.data?.errorCode;
      const msg =
        errorCode && FLOOR_PLAN_ERROR_CODES[errorCode]
          ? t(FLOOR_PLAN_ERROR_CODES[errorCode])
          : err?.response?.data?.message || t('table.floor.plan.error.delete');
      setErrorMessage(msg);
      setShowError(true);
    } finally {
      setDeleteFloorPlanId(null);
    }
  };

  const handleFilterChange = () => {
    fetchFloorPlans();
  };

  // ─── KDS Station CRUD ───────────────────────────────────────────

  const handleOpenKdsDialog = (station?: KdsStation) => {
    setEditingKdsStation(station ?? null);
    setKdsDialogError(undefined);
    setKdsDialogOpen(true);
  };

  const handleCloseKdsDialog = () => {
    setKdsDialogOpen(false);
    setEditingKdsStation(null);
    setKdsDialogError(undefined);
  };

  const handleSaveKdsStation = async (
    data: CreateKdsStationData & { isActive?: boolean }
  ) => {
    try {
      const branchId = currentBranch?.id;
      const branchParams = branchId ? { branchId } : undefined;
      if (editingKdsStation) {
        const updated = await tableService.updateKdsStation(
          editingKdsStation.id,
          data as UpdateKdsStationData,
          branchParams
        );
        setKdsStations((prev) =>
          prev.map((st) => (st.id === updated.id ? updated : st))
        );
        setSuccessMessage(t('table.kds.station.update.success'));
      } else {
        const { isActive: _isActive, ...createData } = data;
        void _isActive;
        const created = await tableService.createKdsStation(
          createData,
          branchParams
        );
        setKdsStations((prev) => [...prev, created]);
        setSuccessMessage(t('table.kds.station.create.success'));
      }
      setKdsDialogError(undefined);
      setKdsDialogOpen(false);
      setEditingKdsStation(null);
      setShowSuccess(true);
    } catch (err: any) {
      const fallback = editingKdsStation
        ? t('table.kds.station.error.update')
        : t('table.kds.station.error.create');
      setKdsDialogError(err?.response?.data?.message || fallback);
      throw err;
    }
  };

  const handleDeleteKdsStationConfirm = async () => {
    if (!deleteKdsStationId) return;
    try {
      const branchId = currentBranch?.id;
      await tableService.deleteKdsStation(
        deleteKdsStationId,
        branchId ? { branchId } : undefined
      );
      setKdsStations((prev) =>
        prev.filter((st) => st.id !== deleteKdsStationId)
      );
      setSuccessMessage(t('table.kds.station.delete.success'));
      setShowSuccess(true);
    } catch (err: any) {
      setErrorMessage(
        err?.response?.data?.message || t('table.kds.station.error.delete')
      );
      setShowError(true);
    } finally {
      setDeleteKdsStationId(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('table.management.title')}</title>
      </Helmet>
      <PageTitleWrapper>
        <PageHeader />
      </PageTitleWrapper>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FloorPlansTable
            floorPlans={floorPlans}
            loading={loadingFloorPlans}
            onAdd={() => handleOpenFloorPlanDialog()}
            onEdit={handleOpenFloorPlanDialog}
            onDelete={(id) => setDeleteFloorPlanId(id)}
            onFilterChange={handleFilterChange}
            pageKey="floorPlans"
          />
        </Grid>
        <Grid item xs={12}>
          <KdsStationsTable
            stations={kdsStations}
            categoryNames={new Map(kdsCategories.map((c) => [c.id, c.name]))}
            loading={loadingKdsStations}
            onAdd={() => handleOpenKdsDialog()}
            onEdit={handleOpenKdsDialog}
            onDelete={(id) => setDeleteKdsStationId(id)}
            onRefresh={fetchKdsStations}
          />
        </Grid>
      </Grid>

      {/* Floor Plan Dialog */}
      <FloorPlanDialog
        open={floorPlanDialogOpen}
        onClose={handleCloseFloorPlanDialog}
        onSave={handleSaveFloorPlan}
        floorPlan={editingFloorPlan}
        error={floorPlanDialogError}
        nextSortOrder={
          editingFloorPlan
            ? undefined
            : Math.max(...floorPlans.map((fp) => fp.sortOrder), -1) + 1
        }
      />

      {/* Delete Floor Plan Confirmation */}
      <ConfirmDialog
        open={Boolean(deleteFloorPlanId)}
        onClose={() => setDeleteFloorPlanId(null)}
        onConfirm={handleDeleteFloorPlanConfirm}
        title={t('table.delete.floor.plan')}
        message={t('table.floor.plan.delete.confirm')}
      />

      {/* KDS Station Dialog */}
      <KdsStationDialog
        open={kdsDialogOpen}
        onClose={handleCloseKdsDialog}
        onSave={handleSaveKdsStation}
        station={editingKdsStation}
        categories={kdsCategories}
        error={kdsDialogError}
        nextSortOrder={
          editingKdsStation
            ? undefined
            : Math.max(...kdsStations.map((st) => st.sortOrder), -1) + 1
        }
      />

      {/* Delete KDS Station Confirmation */}
      <ConfirmDialog
        open={Boolean(deleteKdsStationId)}
        onClose={() => setDeleteKdsStationId(null)}
        onConfirm={handleDeleteKdsStationConfirm}
        title={t('table.kds.station.delete')}
        message={t('table.kds.station.delete.confirm')}
      />

      {/* Success Notification */}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
      >
        <MuiAlert
          variant="filled"
          severity="success"
          onClose={() => setShowSuccess(false)}
        >
          <Typography>{successMessage}</Typography>
        </MuiAlert>
      </Snackbar>

      {/* Error Notification */}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
      >
        <MuiAlert
          variant="filled"
          severity="error"
          onClose={() => setShowError(false)}
        >
          <Typography>{errorMessage}</Typography>
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default TableManagement;
